import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './SubProduct.css';
import { IMAGE_BASE_URL } from '../constant';

const FLAG_MAP = {
  en: '🇬🇧', hi: '🇮🇳', gu: '🇮🇳', mr: '🇮🇳',
  pa: '🇮🇳', bn: '🇮🇳', ta: '🇮🇳', te: '🇮🇳',
  kn: '🇮🇳', ml: '🇮🇳',
};

const SubProduct = () => {
  const { category } = useParams();
  const { currentLanguage } = useLanguage();

  // ── State ──────────────────────────────────────────────────────────────────
  const [product,        setProduct]        = useState(null);
  const [subProducts,    setSubProducts]    = useState([]);
  const [selectedSub,    setSelectedSub]    = useState(null);
  const [content,        setContent]        = useState(null);
  const [languages,      setLanguages]      = useState([]);
  const [activeLang,     setActiveLang]     = useState(currentLanguage || 'en');
  const [activeTab,      setActiveTab]      = useState('details');
  const [pageLoading,    setPageLoading]    = useState(true);   // only for initial load
  const [contentLoading, setContentLoading] = useState(false);  // only for content fetch
  const [error,          setError]          = useState(null);

  // Track if initial load is done — language switches must NOT re-trigger page load
  const initialLoadDone = useRef(false);

  // ── 1. Fetch languages (once) ──────────────────────────────────────────────
  useEffect(() => {
    fetch('/api/languages')
      .then(r => r.json())
      .then(data => setLanguages(Array.isArray(data) && data.length ? data : [
        { code: 'en', name: 'English' },
        { code: 'hi', name: 'Hindi' },
        { code: 'gu', name: 'Gujarati' },
      ]))
      .catch(() => setLanguages([
        { code: 'en', name: 'English' },
        { code: 'hi', name: 'Hindi' },
        { code: 'gu', name: 'Gujarati' },
      ]));
  }, []); // ← empty deps: runs once only

  // ── 2. Fetch product + sub-products (only on category change, NOT lang) ────
  useEffect(() => {
    initialLoadDone.current = false;
    setPageLoading(true);
    setError(null);

    // Fetch parent product (use 'en' for structure, lang only affects display name)
    const productFetch = fetch(`/api/products/${category}?lang=en`)
      .then(r => { if (!r.ok) throw new Error('Product not found'); return r.json(); })
      .then(data => setProduct(data));

    // Fetch sub-products
    const subFetch = fetch(`/api/products/${category}/sub-products?lang=en`)
      .then(r => { if (!r.ok) throw new Error('Failed to load sub-products'); return r.json(); })
      .then(data => {
        setSubProducts(data);
        setSelectedSub(data[0] || null);
        return data;
      });

    Promise.all([productFetch, subFetch])
      .catch(err => setError(err.message))
      .finally(() => {
        setPageLoading(false);
        initialLoadDone.current = true;
      });
  }, [category]); // ← ONLY category, NOT activeLang

  // ── 3. Fetch content (on selectedSub OR activeLang change) ────────────────
  useEffect(() => {
    if (!selectedSub) return;

    setContentLoading(true);
    setContent(null);

    fetch(`/api/content/${selectedSub.id}/${activeLang}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => setContent(data || null))
      .catch(() => setContent(null))
      .finally(() => setContentLoading(false));

  }, [selectedSub, activeLang]); // ← activeLang here only — no page reload

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSubSelect = (sp) => {
    if (sp.id === selectedSub?.id) return;
    setSelectedSub(sp);
    setContent(null);
  };

  // Language switch: ONLY updates activeLang state — no page reload
  const handleLangSwitch = (e, code) => {
    e.preventDefault();           // prevent any default browser behavior
    if (code === activeLang) return; // same lang — do nothing
    setActiveLang(code);          // triggers useEffect #3 only
  };

  // ── Loading / Error screens ────────────────────────────────────────────────
  if (pageLoading) return (
    <div className="sub-product">
      <div className="sp-loading-page">
        <div className="sp-loading-icon">🌱</div>
        <p>Loading product details...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="sub-product">
      <div className="sp-error-page">
        <h2>⚠️ {error}</h2>
        <Link to="/products" className="btn-secondary">← Back to Products</Link>
      </div>
    </div>
  );

  // ── Derived values ─────────────────────────────────────────────────────────
  const displayTitle = content?.title || selectedSub?.title || product?.name || category;
  const displayDesc  = content?.description || selectedSub?.description || '';
  const displayImage = selectedSub?.image_url
    ? `${IMAGE_BASE_URL}${selectedSub.image_url}` : null;
  const displayIcon  = product?.icon || '🌱';

  // ── Render helpers ─────────────────────────────────────────────────────────
  const asRichHtml = (html) => {
    if (!html) return null;
    if (typeof html === 'string' && html.trim().startsWith('<')) {
      return <div className="sp-rich" dangerouslySetInnerHTML={{ __html: html }} />;
    }
    let parsed = html;
    if (typeof html === 'string') {
      try { parsed = JSON.parse(html); } catch { return <p className="sp-plain">{html}</p>; }
    }
    if (Array.isArray(parsed)) {
      return (
        <div className="sp-steps-list">
          {parsed.map((s, i) => (
            <div key={i} className="sp-step-item glass-card">
              <div className="sp-step-num">{s.step || i + 1}</div>
              <div className="sp-step-body">
                <h3>{s.title}</h3>
                <p>{s.description}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }
    return <p className="sp-plain">{String(html)}</p>;
  };

  const renderFeatures = () => {
    if (!content?.features) return null;
    let f = content.features;
    if (typeof f === 'string') { try { f = JSON.parse(f); } catch { return null; } }
    if (!Array.isArray(f)) return null;
    return (
      <ul className="sp-features-list">
        {f.map((item, i) => (
          <li key={i}><span className="sp-check">✓</span>{item}</li>
        ))}
      </ul>
    );
  };

  const renderSpecs = () => {
    if (!content?.specifications) return null;
    let s = content.specifications;
    if (typeof s === 'string') { try { s = JSON.parse(s); } catch { return null; } }
    if (typeof s !== 'object' || Array.isArray(s)) return null;
    return (
      <div className="sp-specs-grid">
        {Object.entries(s).map(([k, v]) => (
          <div key={k} className="sp-spec-card glass-card">
            <h4>{k}</h4>
            <p>{v}</p>
          </div>
        ))}
      </div>
    );
  };

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div className="sub-product">

      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="container">
          <Link to="/">Home</Link><span>/</span>
          <Link to="/products">Products</Link><span>/</span>
          <span>{product?.name || category}</span>
        </div>
      </div>

      {/* Product name + sub-product tabs */}
      <div className="sp-hero-bar">
        <div className="container">
          <h1 className="sp-product-name">{product?.name || category}</h1>
          {subProducts.length > 0 && (
            <div className="sp-sub-tabs">
              {subProducts.map(sp => (
                <button
                  key={sp.id}
                  className={`sp-sub-tab${selectedSub?.id === sp.id ? ' active' : ''}`}
                  onClick={() => handleSubSelect(sp)}
                >
                  {sp.title || sp.slug}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main 2-column layout */}
      <section className="sp-main-section">
        <div className="container">
          <div className="sp-layout">

            {/* LEFT — Image (never re-renders on lang switch) */}
            <div className="sp-left">
              <div className="sp-img-card glass-card">
                {displayImage ? (
                  <>
                    <img
                      src={displayImage}
                      alt={displayTitle}
                      className="sp-img"
                      onError={e => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="sp-img-icon" style={{ display: 'none' }}>{displayIcon}</div>
                  </>
                ) : (
                  <div className="sp-img-icon">{displayIcon}</div>
                )}
              </div>
            </div>

            {/* RIGHT — Content tabs */}
            <div className="sp-right">

              {/* Tab switcher */}
              <div className="sp-content-tabs">
                <button
                  className={`sp-content-tab${activeTab === 'details' ? ' active' : ''}`}
                  onClick={() => setActiveTab('details')}
                >
                  📋 View Details
                </button>
                <button
                  className={`sp-content-tab${activeTab === 'farming' ? ' active' : ''}`}
                  onClick={() => setActiveTab('farming')}
                >
                  🌾 Farming Steps
                </button>
              </div>

              {/* View Details panel — always mounted, CSS hidden when inactive */}
              <div className={`sp-tab-panel glass-card${activeTab !== 'details' ? ' sp-panel-hidden' : ''}`}>
                {contentLoading ? (
                  <div className="sp-inline-loading">
                    <span className="sp-spin">🌱</span> Loading...
                  </div>
                ) : (
                  <>
                    <h2 className="sp-detail-title">{displayTitle}</h2>

                    {displayDesc && (
                      displayDesc.trim().startsWith('<')
                        ? <div className="sp-rich sp-desc" dangerouslySetInnerHTML={{ __html: displayDesc }} />
                        : <p className="sp-desc">{displayDesc}</p>
                    )}

                    {renderFeatures() && (
                      <div className="sp-features-wrap">
                        <h3>Key Features</h3>
                        {renderFeatures()}
                      </div>
                    )}

                    {renderSpecs() && (
                      <div className="sp-specs-wrap">
                        <h3>Specifications</h3>
                        {renderSpecs()}
                      </div>
                    )}

                    {!content && !contentLoading && (
                      <div className="sp-no-content">
                        <span>📋</span>
                        <p>Content not available yet. Please contact us for details.</p>
                      </div>
                    )}

                    <div className="sp-actions">
                      <Link to="/contact" className="btn-primary">Request Quote</Link>
                      <Link to="/contact" className="btn-secondary">Contact Us</Link>
                    </div>
                  </>
                )}
              </div>

              {/* Farming Steps panel — always mounted, CSS hidden when inactive */}
              <div className={`sp-tab-panel glass-card${activeTab !== 'farming' ? ' sp-panel-hidden' : ''}`}>

                {/* Language pills — buttons with e.preventDefault() */}
                <div className="sp-farming-lang">
                  <span className="sp-farming-lang-label">🌐 Language:</span>
                  <div className="sp-lang-pills">
                    {languages.map(lang => (
                      <button
                        key={lang.code}
                        type="button"
                        className={`sp-lang-pill${activeLang === lang.code ? ' active' : ''}`}
                        onClick={(e) => handleLangSwitch(e, lang.code)}
                      >
                        <span className="sp-lang-flag">{FLAG_MAP[lang.code] || '🌐'}</span>
                        <span className="sp-lang-name">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Content area — only this part updates on lang switch */}
                {contentLoading ? (
                  <div className="sp-inline-loading">
                    <span className="sp-spin">🌱</span>
                    <span>Loading {languages.find(l => l.code === activeLang)?.name || ''} content...</span>
                  </div>
                ) : content?.farming_steps ? (
                  <div className="sp-farming-body">
                    {asRichHtml(content.farming_steps)}
                  </div>
                ) : (
                  <div className="sp-no-content">
                    <span>🌾</span>
                    <p>
                      Farming steps not available in{' '}
                      <strong>{languages.find(l => l.code === activeLang)?.name || activeLang}</strong>.
                    </p>
                    <p>Try switching to another language above.</p>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section product-cta">
        <div className="container">
          <div className="cta-card glass-card">
            <h2>Ready to Start Growing?</h2>
            <p>Get in touch with our experts for personalized advice and bulk orders</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary">Contact Us</Link>
              <Link to="/products" className="btn-secondary">View More Products</Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default SubProduct;
